<script setup>
import { useI18n } from 'vue-i18n';
import { computed, ref, useId } from 'vue';

const { t } = useI18n();

/**
 * A labelled input for the account forms. A password field gets a show/hide
 * button and warns while Caps Lock is on; the error (or hint) below is tied
 * to the input for screen readers.
 */
const props = defineProps({
  label: { type: String, required: true },
  type: { type: String, default: 'text' },
  autocomplete: { type: String, default: 'off' },
  error: { type: String, default: '' },
  hint: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
});

const model = defineModel({ type: String, default: '' });
const id = useId();
const input = ref(null);
const revealed = ref(false);
const capsLock = ref(false);

const isPassword = computed(() => props.type === 'password');
const inputType = computed(() => (isPassword.value && revealed.value ? 'text' : props.type));
const describedBy = computed(() => (props.error || props.hint || capsLock.value ? `${id}-note` : undefined));

function trackCapsLock(event) {
  if (isPassword.value && event.getModifierState) capsLock.value = event.getModifierState('CapsLock');
}

defineExpose({ focus: () => input.value?.focus(), select: () => input.value?.select() });
</script>

<template>
  <div>
    <div class="flex items-baseline justify-between gap-3 mb-2">
      <label :for="id" class="text-[13px] 4xl:text-sm font-medium text-deen-ink/75">{{ label }}</label>
      <slot name="aside" />
    </div>
    <div class="relative">
      <input
        :id="id"
        ref="input"
        v-model="model"
        :type="inputType"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :required="required"
        :aria-invalid="error ? 'true' : undefined"
        :aria-describedby="describedBy"
        autocapitalize="off"
        spellcheck="false"
        :class="[
          'auth-input w-full h-12 4xl:h-14 rounded-2xl bg-deen-mist focus:bg-white border px-4 text-[15px] 4xl:text-lg text-deen-ink placeholder:text-deen-ink/30 transition-[border-color,box-shadow] duration-150',
          'focus:outline-none focus:ring-[3px] disabled:opacity-50',
          isPassword ? 'pr-12' : '',
          error ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : 'border-deen-ink/10 hover:border-deen-ink/25 focus:border-deen-ink/50 focus:ring-deen-gold/40',
        ]"
        @keydown="trackCapsLock"
        @keyup="trackCapsLock"
        @blur="capsLock = false"
      />
      <button
        v-if="isPassword"
        type="button"
        class="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-xl flex items-center justify-center text-deen-ink/40 hover:text-deen-ink hover:bg-deen-ink/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-deen-gold"
        :aria-label="revealed ? t('study.password.hide') : t('study.password.show')"
        :aria-pressed="revealed"
        @click="revealed = !revealed"
      >
        <iconify-icon :icon="revealed ? 'solar:eye-closed-linear' : 'solar:eye-linear'" class="text-lg" aria-hidden="true" />
      </button>
    </div>
    <p v-if="error" :id="`${id}-note`" class="mt-1.5 flex items-start gap-1.5 text-xs text-red-600">
      <iconify-icon icon="solar:danger-circle-linear" class="text-sm mt-px shrink-0" aria-hidden="true" />{{ error }}
    </p>
    <p v-else-if="capsLock" :id="`${id}-note`" class="mt-1.5 flex items-center gap-1.5 text-xs text-amber-700">
      <iconify-icon icon="solar:keyboard-linear" class="text-sm shrink-0" aria-hidden="true" />{{ t('study.password.caps') }}
    </p>
    <p v-else-if="hint" :id="`${id}-note`" class="mt-1.5 text-xs text-deen-ink/45">{{ hint }}</p>
  </div>
</template>

<style scoped>
/* Chrome paints autofilled fields light blue; keep them white like the rest. */
.auth-input:-webkit-autofill,
.auth-input:-webkit-autofill:hover,
.auth-input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px #fff inset;
  -webkit-text-fill-color: rgb(28 72 72);
  caret-color: rgb(28 72 72);
}
</style>
